import { ChevronDownIcon, ChevronRightIcon } from 'lucide-react-native';
import type { ReactNode } from 'react';
import { twJoin } from 'tailwind-merge';

import { Divider } from '@/components/ui/divider';
import { HStack } from '@/components/ui/hstack';
import { Pressable } from '@/components/ui/pressable';
import { Text } from '@/components/ui/text';
import { VStack } from '@/components/ui/vstack';
import { useColors } from '@/hooks/useColors';
import { getAccessibilityProps } from '@/util/accessibilityProps';
import { TOUCH_TARGET_MIN_H } from '@/util/accessibleTouchTarget';

type RowTrailing = 'chevron-right' | 'chevron-down';

/**
 * The inside of an Account list row: title, optional badge and subtitle, the
 * chevron, and the divider. Split from
 * `AccountListRow` so a control that must be its own pressable, such as the
 * select trigger on the act-as row, can draw the same row without nesting one
 * pressable in another.
 */
export function AccountListRowContent({
  title,
  subtitle,
  badge,
  trailing = 'chevron-right',
  wrapContent,
}: {
  title: string;
  subtitle?: string;
  badge?: ReactNode;
  /** `chevron-down` marks a row that opens a list of choices rather than going to another screen. */
  trailing?: RowTrailing;
  /** Wraps the title line and chevron without the divider, such as to highlight just that part. */
  wrapContent?: (content: ReactNode) => ReactNode;
}) {
  const { colors } = useColors();

  const isChoice = trailing === 'chevron-down';
  const TrailingIcon = isChoice ? ChevronDownIcon : ChevronRightIcon;
  const trailingColor = isChoice ? colors.typography[600] : colors.primary[500];

  const content = (
    <HStack className="w-full items-center justify-between gap-2">
      <VStack className="min-w-0 flex-1">
        <HStack className="min-w-0 items-center gap-2">
          <Text numberOfLines={1} className="shrink">
            {title}
          </Text>

          {badge}
        </HStack>

        {subtitle ? (
          <Text size="sm" numberOfLines={2} className="text-typography-600">
            {subtitle}
          </Text>
        ) : null}
      </VStack>

      {/* A choice row's caret sits on the title's line, and its box matches the other rows' chevrons so the right edges line up. */}
      <HStack
        className={twJoin(
          'min-w-0 max-w-[45%] shrink items-center gap-1',
          isChoice && 'self-start'
        )}>
        <TrailingIcon size={24} color={trailingColor} />
      </HStack>
    </HStack>
  );

  return (
    <>
      {wrapContent ? wrapContent(content) : content}

      <Divider />
    </>
  );
}

/** The classes every row's pressable shares, so a select trigger can match a plain row. */
export const ACCOUNT_LIST_ROW_CLASS = twJoin(
  'w-full flex-col justify-center gap-4',
  TOUCH_TARGET_MIN_H
);

/**
 * One tappable row in an Account list. Shared by the Account screen's Activity
 * and Settings lists and the poll history screen, so they read as one list
 * rather than drifting apart.
 */
export function AccountListRow({
  title,
  subtitle,
  badge,
  accessibilityLabel,
  hint,
  trailing,
  wrapContent,
  onPress,
}: {
  title: string;
  subtitle?: string;
  /** Status pill rendered beside the title, e.g. `<Pill tone="warning">Incomplete</Pill>`. */
  badge?: ReactNode;
  /**
   * What a screen reader announces. Defaults to the title and subtitle together,
   * and to the visible content when the row has a badge, since a badge's text
   * would otherwise be dropped from what is read out.
   */
  accessibilityLabel?: string;
  /** Extra spoken context, such as what the row opens. */
  hint?: string;
  trailing?: RowTrailing;
  /** See `AccountListRowContent`. */
  wrapContent?: (content: ReactNode) => ReactNode;
  onPress: () => void;
}) {
  const defaultLabel = subtitle && !badge ? `${title}, ${subtitle}` : undefined;

  return (
    <Pressable
      onPress={onPress}
      className={ACCOUNT_LIST_ROW_CLASS}
      {...getAccessibilityProps({
        role: 'button',
        label: accessibilityLabel ?? defaultLabel,
        hint,
      })}>
      <AccountListRowContent
        title={title}
        subtitle={subtitle}
        badge={badge}
        trailing={trailing}
        wrapContent={wrapContent}
      />
    </Pressable>
  );
}
